import { clientMoveIn } from "../../lib/clientMoveIn";
import { useTaskWorkflowWritesEnabled } from '../../hooks/useTaskWorkflowWritesEnabled';
import TaskTransitionDialog from './TaskTransitionDialog';
﻿// src/components/clients/DetailDrawer.tsx
import React, { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  Typography,
  Avatar,
  Chip,
  Stack,
  IconButton,
  Button,
  Tabs,
  Tab,
  Paper,
  Grid,
  Divider,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  Menu,
  MenuItem,
  ListItemIcon as MuiListItemIcon,
  ListItemText,
  TextField,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Tooltip,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
  History as HistoryIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  NoteAdd as NoteAddIcon,
  Assignment as TaskIcon,
  Search as SearchIcon,
  ContactMail as ContactMailIcon,
  PlayArrow as PlayArrowIcon,
  Visibility as VisibilityIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Receipt as ReceiptIcon,
  PictureAsPdf as PdfIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { gradients } from "../../theme/theme";
import { supabase } from "../../lib/supabase";
import { notifyClientAssigned } from "../../services/assignmentNotification";
import type {
  Client,
  ClientStatus,
  Agent,
  ClientActivity,
  ClientNote,
  ClientTask,
  SentListing,
  SentGuestCard,
  SavedSearch,
} from "./types";
import {
  statusConfig,
  statusActions,
  shortlistActionLabel,
  priorityConfig,
  taskTypeOptions,
} from "./config";
const SHORTLIST_OUTCOME_UI_ENABLED = process.env.REACT_APP_SHORTLIST_OUTCOME_UI_ENABLED === "true";
import {
  formatRelativeTime,
  getNextStatus,
  getActivityIcon,
  getActivityColor,
  formatActivityDescription,
  isMobileDevice,
} from "./helpers";
import EmailPreviewModal from "./EmailPreviewModal";
import SendInvoiceModal from "./SendInvoiceModal";
import InvoicePdfModal from "./InvoicePdfModal";
import type { ClientStageSync, Invoice } from "./invoiceTypes";
import { useEmailTracking } from "../../hooks/useEmailTracking";
import { usePersistedState } from "../../hooks/usePersistedState";
import EmailTrackingBadge from "./EmailTrackingBadge";
// PKT-0043a Sidebar Timeline merge v1 · Phase 1 · tab components
import ClientTimeline from "./ClientTimeline";
import ClientShortlistPanel from "./ClientShortlistPanel";
import { useShortlistHasItems } from "../../hooks/useShortlistHasItems";
import IntentProfilePanel, { IntentProfileView } from "./IntentProfilePanel";
import { fetchWithAuth } from "../../lib/api";
import ClientMessagesPanel from "./ClientMessagesPanel";
import ClientFilesPanel from "./ClientFilesPanel";
// Director-direct recovery 2026-05-17 · pipeline strip + Timeline inline actions
// restoring operator workflow PR #118 dropped from per-tab UIs
import StatusPipeline from "./StatusPipeline";

interface DetailDrawerProps {
  client: Client | null;
  open: boolean;
  onClose: () => void;
  onEdit: (client: Client) => void;
  onQuickAction: (action: string, client: Client) => void;
  onStatusChange: (client: Client, newStatus: ClientStatus) => void;
  onRefresh: () => void;
  agents: Agent[];
  currentUserRole: string;
  initialTab?: string; // tab key to open on mount, e.g. "activity"
  onTabChange?: (tab: string) => void;
  openAction?: "addNote" | "addTask" | null; // auto-trigger an action on mount
}

const formatPhoneDisplay = (raw: string | null | undefined): string => {
  if (!raw) return "";
  const digits = raw.replace(/[^\d]/g, "");
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits.startsWith("1")) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return raw;
};

const DetailDrawer: React.FC<DetailDrawerProps> = ({
  client,
  open,
  onClose,
  onEdit,
  onQuickAction,
  onStatusChange,
  onRefresh,
  agents,
  currentUserRole,
  initialTab,
  onTabChange,
  openAction,
}) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = usePersistedState<number>(
    "clientDetail.activeTab",
    0
  );
  const notesSectionRef = React.useRef<HTMLDivElement | null>(null);
  const contentScrollRef = React.useRef<HTMLDivElement | null>(null);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
  const [priorityMenuAnchor, setPriorityMenuAnchor] = useState<null | HTMLElement>(null);

  // Data states
  const [activities, setActivities] = useState<ClientActivity[]>([]);
  const [notes, setNotes] = useState<ClientNote[]>([]);
  const [tasks, setTasks] = useState<ClientTask[]>([]);
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [sentListings, setSentListings] = useState<SentListing[]>([]);
  const [sentGuestCards, setSentGuestCards] = useState<SentGuestCard[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [intentProfile, setIntentProfile] = useState<IntentProfileView | null>(null);

  // Loading states
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [loadingSearches, setLoadingSearches] = useState(false);
  const [loadingListings, setLoadingListings] = useState(false);
  const [loadingGuestCards, setLoadingGuestCards] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  useEffect(() => {
    if (!open || !client?.id || process.env.REACT_APP_INTENT_PROFILE_UI_ENABLED !== "true") {
      setIntentProfile(null);
      return;
    }
    fetchWithAuth<IntentProfileView | null>(`/intent-profiles/client/${client.id}`)
      .then(setIntentProfile)
      .catch((error) => {
        console.warn("intent_profile_load_failed", error);
        setIntentProfile(null);
      });
  }, [open, client?.id]);

  // Dialog states
  const [addNoteDialogOpen, setAddNoteDialogOpen] = useState(false);
  const taskWorkflowWritesEnabled = useTaskWorkflowWritesEnabled(open ? client?.id : null);
  const [taskTransition, setTaskTransition] = React.useState<{ id: string; action: 'complete' | 'cancel' } | null>(null);
  React.useEffect(() => setTaskTransition(null), [client?.id]);
  const [addTaskDialogOpen, setAddTaskDialogOpen] = useState(false);
  // PKT-0043a v1a regression fix · the old activeTab===0 Notes block carried
  // the manager agent-reassignment inline form. When the tabs collapsed to 3,
  // the form's mount site disappeared but the handlers (handleAgentChange ·
  // handleSaveAgent · handleCancelAgentChange) + state (selectedAgentId ·
  // agentChanged · savingAgent) all stayed. This Dialog re-wires them so the
  // Zone 3 agent click-to-change affordance actually works again.
  const [agentEditOpen, setAgentEditOpen] = useState(false);
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [sendInvoiceOpen, setSendInvoiceOpen] = useState(false);
  const [invoicePdfModalOpen, setInvoicePdfModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [selectedEmail, setSelectedEmail] = useState<{
    title: string;
    html?: string;
    metadata?: any;
  } | null>(null);

  // Form states
  const [newNote, setNewNote] = useState("");
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskType, setNewTaskType] = useState("follow_up");
  const [newTaskDescription, setNewTaskDescription] = useState("");
  const [newTaskDueDate, setNewTaskDueDate] = useState("");

  // Agent assignment state
  const [selectedAgentId, setSelectedAgentId] = useState<string>("");
  const [agentChanged, setAgentChanged] = useState(false);
  const [savingAgent, setSavingAgent] = useState(false);
  const canManageAssignments =
    currentUserRole === "admin" || currentUserRole === "manager";

  useEffect(() => {
    if (client && open) {
      loadAllData(client.id);
      // Sidebar tabs are Timeline · List · Files. Old keys from the prior
      // drawer shape migrate into the closest current surface.
      const tabKeyToIndex: Record<string, number> = {
        details: 0,
        activity: 0,
        searches: 0,
        guestcards: 0,
        invoices: 0,
        timeline: 0,
        tasks: 0,
        shortlist: 1,
        list: 1,
        files: 2,
        // TRAIN-3 deep link target. For agents the tabs array is 3 long and
        // the bound clamp lands them on Timeline — D9 unchanged.
        messages: 3,
      };
      // For addNote auto-action, force Timeline (the Notes input lives there now)
      const effectiveInitialTab =
        openAction === "addNote" ? "timeline" : initialTab;
      const requestedIndex =
        effectiveInitialTab && tabKeyToIndex[effectiveInitialTab] !== undefined
          ? tabKeyToIndex[effectiveInitialTab]
          : 0;
      setActiveTab(
        requestedIndex === 3 && !canManageAssignments ? 0 : requestedIndex
      );
      setSelectedAgentId(client.assigned_agent_id || "");
      setAgentChanged(false);

      // Auto-trigger add-note or add-task after the drawer mounts
      if (openAction) {
        const timer = setTimeout(() => {
          if (openAction === "addNote") {
            notesSectionRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
            setAddNoteDialogOpen(true);
          } else if (openAction === "addTask") {
            setAddTaskDialogOpen(true);
          }
        }, 300);
        return () => clearTimeout(timer);
      }
    }
  }, [client, open, initialTab, openAction, canManageAssignments]);

  const loadAllData = async (clientId: string) => {
    loadActivities(clientId);
    loadNotes(clientId);
    loadTasks(clientId);
    loadSavedSearches(clientId);
    loadSentListings(clientId);
    loadSentGuestCards(clientId);
    loadInvoices(clientId);
  };

  const loadActivities = async (clientId: string) => {
    setLoadingActivities(true);
    try {
      const { data, error } = await supabase
        .from("activities")
        .select("*")
        .eq("entity_id", clientId)
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;

      const uniqueActivities = (data || []).reduce((acc: any[], curr: any) => {
        if (!acc.find((a) => a.id === curr.id)) {
          acc.push(curr);
        }
        return acc;
      }, []);

      const mappedActivities: ClientActivity[] = uniqueActivities.map(
        (a: any) => ({
          id: a.id,
          activity_type: a.activity_type,
          description: formatActivityDescription(a.activity_type, a.metadata),
          metadata: a.metadata,
          created_at: a.created_at,
        })
      );
      setActivities(mappedActivities);
    } catch (err) {
      console.error("Error loading activities:", err);
      setActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  };

  const loadNotes = async (clientId: string) => {
    setLoadingNotes(true);
    try {
      const { data, error } = await supabase
        .from("client_notes")
        .select("*")
        .eq("client_id", clientId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      setNotes(data || []);
    } catch (err) {
      console.error("Error loading notes:", err);
      setNotes([]);
    } finally {
      setLoadingNotes(false);
    }
  };

  const loadTasks = async (clientId: string) => {
    setLoadingTasks(true);
    try {
      const taskSelect: string = SHORTLIST_OUTCOME_UI_ENABLED
        ? "*, question_payload:shortlist_question_payloads(question_text,deleted_at)"
        : "*";
      const { data, error } = await supabase
        .from("client_tasks")
        .select(taskSelect as any)
        .eq("client_id", clientId)
        .order("status", { ascending: true })
        .order("due_date", { ascending: true, nullsFirst: false })
        .order("created_at", { ascending: false });
      if (error) throw error;
      const sorted = ((data || []) as unknown as ClientTask[]).sort((a, b) => {
        const statusOrder = { pending: 0, completed: 1, cancelled: 2 };
        return (
          (statusOrder[a.status as keyof typeof statusOrder] || 0) -
          (statusOrder[b.status as keyof typeof statusOrder] || 0)
        );
      });
      setTasks(sorted);
    } catch (err) {
      console.error("Error loading tasks:", err);
      setTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  const loadSavedSearches = async (clientId: string) => {
    setLoadingSearches(true);
    try {
      const { data, error } = await supabase
        .from("saved_searches")
        .select("*")
        .eq("client_id", clientId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      setSavedSearches(data || []);
    } catch (err) {
      console.error("Error loading saved searches:", err);
      setSavedSearches([]);
    } finally {
      setLoadingSearches(false);
    }
  };

  const loadSentListings = async (clientId: string) => {
    setLoadingListings(true);
    try {
      const { data, error } = await supabase
        .from("clients")
        .select("sent_list")
        .eq("id", clientId)
        .single();

      if (error) throw error;

      const sentListData = data?.sent_list || [];
      const mappedListings: SentListing[] = sentListData.map(
        (item: any, index: number) => ({
          id: `${clientId}-${index}`,
          client_id: clientId,
          recipient_email: item.recipient_email,
          sent_at: item.sent_at,
          tracking_id: item.tracking_id,
          email_html: item.email_html,
          property_count: item.properties?.length || 0,
          unit_count:
            item.properties?.reduce(
              (sum: number, p: any) => sum + (p.units?.length || 0),
              0
            ) || 0,
          properties: item.properties,
          subject: item.subject,
          locator: item.locator,
          personal_message: item.personal_message,
        })
      );

      mappedListings.sort(
        (a, b) => new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime()
      );

      setSentListings(mappedListings);
    } catch (err) {
      console.error("Error loading sent listings:", err);
      setSentListings([]);
    } finally {
      setLoadingListings(false);
    }
  };

  const loadSentGuestCards = async (clientId: string) => {
    setLoadingGuestCards(true);
    try {
      const { data, error } = await supabase
        .from("sent_guest_cards")
        .select("*")
        .eq("client_id", clientId)
        .order("sent_at", { ascending: false });
      if (error) throw error;
      setSentGuestCards(data || []);
    } catch (err) {
      console.error("Error loading guest cards:", err);
      setSentGuestCards([]);
    } finally {
      setLoadingGuestCards(false);
    }
  };

  const loadInvoices = async (clientId: string) => {
    setLoadingInvoices(true);
    try {
      const { data, error } = await supabase
        .from("invoices")
        .select("*")
        .eq("client_id", clientId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      setInvoices(data || []);
    } catch (err) {
      console.error("Error loading invoices:", err);
      setInvoices([]);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleAddNote = async () => {
    if (!client || !newNote.trim()) return;
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const { error: noteError } = await supabase.from("client_notes").insert({
        client_id: client.id,
        content: newNote,
        created_by: user.id,
      });
      if (noteError) throw noteError;

      await supabase.from("activities").insert({
        user_id: user.id,
        activity_type: "note_added",
        entity_type: "client",
        entity_id: client.id,
        metadata: {
          client_name: `${client.first_name} ${client.last_name}`,
          note_preview: newNote.substring(0, 100),
        },
      });

      setNewNote("");
      setAddNoteDialogOpen(false);
      loadNotes(client.id);
      loadActivities(client.id);
    } catch (err) {
      console.error("Error adding note:", err);
    }
  };

  const handleAddTask = async () => {
    if (!client || !newTaskTitle.trim() || !newTaskType) return;
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const { error: taskError } = await supabase.from("client_tasks").insert({
        client_id: client.id,
        task_type: newTaskType,
        title: newTaskTitle,
        description: newTaskDescription || null,
        due_date: newTaskDueDate || null,
        status: "pending",
        created_by: user.id,
      });
      if (taskError) throw taskError;

      await supabase.from("activities").insert({
        user_id: user.id,
        activity_type: "task_created",
        entity_type: "client",
        entity_id: client.id,
        metadata: {
          client_name: `${client.first_name} ${client.last_name}`,
          title: newTaskTitle,
          task_type: newTaskType,
        },
      });

      setNewTaskTitle("");
      setNewTaskType("follow_up");
      setNewTaskDescription("");
      setNewTaskDueDate("");
      setAddTaskDialogOpen(false);
      loadTasks(client.id);
      loadActivities(client.id);
    } catch (err) {
      console.error("Error adding task:", err);
    }
  };

  const handleToggleTask = async (task: ClientTask) => {
    if (!client) return;
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const newStatus = task.status === "completed" ? "pending" : "completed";
      await supabase
        .from("client_tasks")
        .update({
          status: newStatus,
          completed_at:
            newStatus === "completed" ? new Date().toISOString() : null,
        })
        .eq("id", task.id);

      if (newStatus === "completed") {
        await supabase.from("activities").insert({
          user_id: user.id,
          activity_type: "task_completed",
          entity_type: "client",
          entity_id: client.id,
          metadata: {
            client_name: `${client.first_name} ${client.last_name}`,
            title: task.title,
          },
        });
      }

      loadTasks(client.id);
      loadActivities(client.id);
    } catch (err) {
      console.error("Error toggling task:", err);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await supabase.from("client_tasks").delete().eq("id", taskId);
      if (client) loadTasks(client.id);
    } catch (err) {
      console.error("Error deleting task:", err);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await supabase.from("client_notes").delete().eq("id", noteId);
      if (client) loadNotes(client.id);
    } catch (err) {
      console.error("Error deleting note:", err);
    }
  };

  const handleAgentChange = (newAgentId: string) => {
    setSelectedAgentId(newAgentId);
    const originalAgentId = client?.assigned_agent_id || "";
    setAgentChanged(newAgentId !== originalAgentId);
  };

  const handleSaveAgent = async () => {
    if (!client) return;
    setSavingAgent(true);
    const previousAgentId = client.assigned_agent_id || null;
    try {
      // PostgREST returns { error } rather than throwing, so discarding this
      // result meant a DENIED reassignment still ran the success path: the form
      // reset, the drawer refreshed, and the bell fired for an assignment that
      // never happened. ClientList.tsx:906-910 already does it this way.
      const { error: updateError } = await supabase
        .from("clients")
        .update({ assigned_agent_id: selectedAgentId || null })
        .eq("id", client.id);
      if (updateError) throw updateError;
      setAgentChanged(false);

      // Tell the new agent. Fire-and-forget: the assignment itself succeeded,
      // and a bell failure must never make the save look failed.
      supabase.auth
        .getUser()
        .then(({ data }) =>
          notifyClientAssigned({
            clientId: client.id,
            clientFirstName: client.first_name,
            clientLastName: client.last_name,
            newAgentId: selectedAgentId || null,
            previousAgentId,
            actingUserId: data.user?.id,
          })
        )
        .catch((err) =>
          console.error("Assignment notification failed:", err)
        );

      onRefresh();
    } catch (err) {
      console.error("Error updating agent:", err);
    } finally {
      setSavingAgent(false);
    }
  };

  const handleCancelAgentChange = () => {
    setSelectedAgentId(client?.assigned_agent_id || "");
    setAgentChanged(false);
  };

  const viewListingEmail = (listing: SentListing) => {
    setSelectedEmail({
      title: "Listing Email",
      html: listing.email_html,
      metadata: {
        recipient: listing.recipient_email,
        sentAt: listing.sent_at,
        propertyCount: listing.property_count,
        unitCount: listing.unit_count,
      },
    });
    setEmailPreviewOpen(true);
  };

  const viewGuestCardEmail = (guestCard: SentGuestCard) => {
    setSelectedEmail({
      title: "Guest Card Email",
      html: guestCard.email_html,
      metadata: {
        recipient: guestCard.recipient_email,
        sentAt: guestCard.sent_at,
        propertyName: guestCard.property_snapshot?.name,
      },
    });
    setEmailPreviewOpen(true);
  };

  const handleInvoiceSuccess = () => {
    if (client) {
      loadActivities(client.id);
      loadInvoices(client.id);
    }
    onRefresh();
  };

  const [demoInvoiceDialogOpen, setDemoInvoiceDialogOpen] = useState(false);

  const handleQuickAction = (actionKey: string, clientData: Client) => {
    if (actionKey === "send_invoice") {
      if (currentUserRole === "demo") {
        setDemoInvoiceDialogOpen(true);
      } else {
        setSendInvoiceOpen(true);
      }
    } else {
      onQuickAction(actionKey, clientData);
    }
  };

  const getInvoiceStatusColor = (status: string) => {
    switch (status) {
      case "sent":
        return { bg: "#3b82f620", color: "#60a5fa" };
      case "paid":
        return { bg: "#22c55e20", color: "#4ade80" };
      case "overdue":
        return { bg: "#ef444420", color: "#f87171" };
      case "cancelled":
        return { bg: "#64748b20", color: "#94a3b8" };
      case "failed":
        return { bg: "#f9731620", color: "#fb923c" };
      default:
        return { bg: "#64748b20", color: "#94a3b8" };
    }
  };

  // Open the PDF modal instead of navigating to a URL
  const handleViewInvoicePdf = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setInvoicePdfModalOpen(true);
  };

  // Handle invoice status change from the modal
  const handleInvoiceStatusChange = (
    invoiceId: string,
    newStatus: string,
    stageSync?: ClientStageSync
  ) => {
    setInvoices((prev) =>
      prev.map((inv) =>
        inv.id === invoiceId
          ? { ...inv, status: newStatus as Invoice["status"] }
          : inv
      )
    );
    if (selectedInvoice?.id === invoiceId) {
      setSelectedInvoice((prev) =>
        prev ? { ...prev, status: newStatus as Invoice["status"] } : null
      );
    }
    if (client) {
      loadActivities(client.id);
      // Confirmed invoice status changes also affect parent invoice filters,
      // even when the client's stage did not move.
      onRefresh();
      if (stageSync?.note) loadNotes(client.id);
    }
  };

  const handlePriorityChange = async (newPriority: string) => {
    if (!client) return;
    try {
      await supabase
        .from("clients")
        .update({ priority: newPriority })
        .eq("id", client.id);
      onRefresh();
    } catch (err) {
      console.error("Failed to update priority:", err);
    }
  };

  const trackingIds = Array.from(
    new Set(
      [
        ...sentListings.map((listing) => listing.tracking_id),
        ...sentGuestCards.map((guestCard) => guestCard.tracking_id),
        ...invoices.map((invoice) => invoice.tracking_id),
        ...activities.map((activity) =>
          typeof activity.metadata?.tracking_id === "string"
            ? activity.metadata.tracking_id
            : undefined
        ),
      ].filter((trackingId): trackingId is string => Boolean(trackingId))
    )
  );

  const { trackingMap } = useEmailTracking(open ? trackingIds : []);

  const getTrackingData = (trackingId?: string) => {
    if (!trackingId) return undefined;
    return trackingMap.get(trackingId);
  };

  // Before the early return: hooks run on every render (PKT-0841 label).
  const shortlistHasItems = useShortlistHasItems(client?.id);
  if (!client) return null;

  const statusInfo = statusConfig[client.status];
  const priorityInfo = priorityConfig[client.priority];
  const nextStatus = getNextStatus(client.status);
  const isMobile = isMobileDevice();
  const formattedPhone = formatPhoneDisplay(client.phone);
  const actions = (statusActions[client.status] || []).map((a) => {
    if (!isMobile && a.key === "sent_text") return { ...a, label: "Log Text" };
    if (!isMobile && a.key === "call") return { ...a, label: "Log Call" };
    if (a.key === "send_guest_card") return { ...a, label: "Guest Card" };
    if (a.key === "send_list") return { ...a, label: shortlistActionLabel(shortlistHasItems) };
    return a;
  });
  const getCompactMobileActionLabel = (actionKey: string, fallbackLabel: string) => {
    const compactMap: Record<string, string> = {
      sent_text: "Text",
      call: "Call",
      send_list: shortlistActionLabel(shortlistHasItems),
      send_guest_card: "Guest Card",
      upload_lease: "Lease",
      send_invoice: "Invoice",
      send_gift_card: "Gift Card",
    };
    return compactMap[actionKey] || fallbackLabel;
  };
  const currentAgent =
    agents.find((a) => a.id === client.assigned_agent_id) ||
    client.assigned_agent;
  const assignedAgentDisplay =
    currentAgent?.full_name || currentAgent?.email || "Unassigned";
  const hasInvoices = invoices.length > 0;

  // Sidebar tab model:
  //   Timeline  · open tasks, quick log, and chronological client history
  //   Shortlist · active property list plus the public client link
  //   Files    · sent lists, guest cards, invoices/PDFs, and future uploads
  const tabs: { label: React.ReactNode; key: string }[] = [
    { label: "Timeline", key: "timeline" },
    { label: "Shortlist", key: "list" },
    { label: "Files", key: "files" },
  ];
  // TRAIN-2 · Messages tab, manager/admin ONLY per train D9 (Grant verbatim:
  // "this will start as an admin/manager feature only, then we will extend to
  // agents (sloane) after testing it"). Agents keep exactly the tabs they had;
  // the API enforces the same boundary server-side.
  if (canManageAssignments) {
    tabs.push({ label: "Messages", key: "messages" });
  }

  const visibleActiveTab = activeTab < tabs.length ? activeTab : 0;

  return (
    <>
      {taskWorkflowWritesEnabled && taskTransition && tasks.find(task => task.id === taskTransition.id) && <TaskTransitionDialog
        key={taskTransition.id + taskTransition.action}
        task={tasks.find(task => task.id === taskTransition.id)!} action={taskTransition.action}
        onClose={() => setTaskTransition(null)} onSaved={() => { loadTasks(client.id); loadActivities(client.id); }} />}
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: { xs: "100%", md: 560 } }, "data-tour": "client-detail-drawer" } as any}
      >
        {/* Zone 1 â€” darkest bar: close + edit only · slim */}
        <Box
          sx={{
            px: 1.5,
            py: 0.5,
            background: "#0b1220",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close client details"
            sx={{ color: "white" }}
            data-tour="detail-drawer-close"
          >
            <CloseIcon />
          </IconButton>
          <Tooltip title="Edit client">
            <IconButton
              onClick={() => onEdit(client)}
              size="small"
              sx={{ color: "white" }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Zone 2 â€” dark client section: name + pills + Move to + action buttons */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #1e293b 0%, #162032 100%)",
            color: "white",
            px: 2,
            pt: 1.25,
            pb: 1.25,
          }}
        >
          {/* Director-direct redesign 2026-05-17:
              Removed redundant status chip (pipeline below shows current status)
              + replaced verbose priority chip with compact emoji button shown
              only when priority != normal. Pipeline gets its own full-width row.
              Out-of-pipeline status changes (out_of_contact · closed) move to
              the edit pencil at top-right (full client edit form). */}
          <Box sx={{ width: "100%" }}>
            {/* Name row · name on left · priority + agent on right
                Director-direct redesign 2026-05-17 per Grant "agent inline
                with name · cleaner details" */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 0.75,
              }}
            >
              <Typography
                variant="h6"
                fontWeight={700}
                color="white"
                sx={{ lineHeight: 1.2, flex: 1, minWidth: 0 }}
                noWrap
              >
                {client.first_name} {client.last_name}
              </Typography>
              {client.priority !== "normal" && (
                <Tooltip title={`Priority · ${priorityInfo.label} · click to change`} arrow>
                  <IconButton
                    size="small"
                    onClick={(e) => setPriorityMenuAnchor(e.currentTarget)}
                    sx={{
                      p: 0.25,
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.08)",
                      },
                    }}
                  >
                    <Box component="span" sx={{ fontSize: 16, lineHeight: 1 }}>
                      {client.priority === "urgent"
                        ? "🚨"
                        : client.priority === "high"
                        ? "⚠️"
                        : "💤"}
                    </Box>
                  </IconButton>
                </Tooltip>
              )}
              <Menu
                anchorEl={priorityMenuAnchor}
                open={Boolean(priorityMenuAnchor)}
                onClose={() => setPriorityMenuAnchor(null)}
              >
                {Object.entries(priorityConfig).map(([key, { label, color }]) => (
                  <MenuItem
                    key={key}
                    selected={client.priority === key}
                    onClick={() => {
                      handlePriorityChange(key);
                      setPriorityMenuAnchor(null);
                    }}
                  >
                    <MuiListItemIcon>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          backgroundColor: color,
                        }}
                      />
                    </MuiListItemIcon>
                    <ListItemText>{label}</ListItemText>
                  </MenuItem>
                ))}
              </Menu>
              {/* Agent · inline with name · right edge */}
              <Tooltip
                title={
                  canManageAssignments
                    ? "Click to change assigned agent"
                    : `Assigned to ${assignedAgentDisplay}`
                }
                placement="left"
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.6,
                    cursor: canManageAssignments ? "pointer" : "default",
                    px: 0.75,
                    py: 0.35,
                    borderRadius: 1,
                    "&:hover": canManageAssignments
                      ? { backgroundColor: "rgba(255,255,255,0.08)" }
                      : undefined,
                  }}
                  onClick={() => {
                    if (!canManageAssignments) return;
                    setSelectedAgentId(client?.assigned_agent_id || "");
                    setAgentChanged(false);
                    setAgentEditOpen(true);
                  }}
                >
                  <Avatar
                    src={currentAgent?.avatar_url}
                    sx={{
                      width: 22,
                      height: 22,
                      fontSize: "0.7rem",
                      background: gradients.primary,
                    }}
                  >
                    {assignedAgentDisplay.charAt(0)}
                  </Avatar>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      color: "rgba(255,255,255,0.92)",
                    }}
                    noWrap
                  >
                    {assignedAgentDisplay}
                  </Typography>
                </Box>
              </Tooltip>
            </Box>

            {/* Details row · contact on left · preferences right-justified ·
                fills the full sidebar width · wraps on narrow screens.
                Director-direct redesign 2026-05-17 per Grant "right side has
                unused space · use it for details". */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1,
                mb: 1.5,
              }}
            >
              {/* Contact cluster · left */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 0.75,
                  minWidth: 0,
                }}
              >
                {client.email && (
                  <Typography
                    variant="caption"
                    component="a"
                    href={`mailto:${client.email}`}
                    noWrap
                    sx={{
                      color: "#7CC8FF",
                      textDecoration: "none",
                      fontSize: "0.78rem",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {client.email}
                  </Typography>
                )}
                {client.email && client.phone && (
                  <Box component="span" sx={{ color: "rgba(255,255,255,0.35)", fontSize: "0.78rem" }}>
                    ·
                  </Box>
                )}
                {client.phone && (
                  <Typography
                    variant="caption"
                    component="a"
                    href={`tel:${(client.phone || "").replace(/[^\d+]/g, "")}`}
                    sx={{
                      color: "#7CC8FF",
                      textDecoration: "none",
                      fontSize: "0.78rem",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {formattedPhone}
                  </Typography>
                )}
              </Box>

              {/* Preferences cluster · right-justified · pushed to far edge */}
              {(client.max_budget ||
                (client.min_bedrooms !== undefined && client.min_bedrooms !== null) ||
                clientMoveIn(client) ||
                (client.preferred_areas && client.preferred_areas.length > 0)) && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 0.75,
                  }}
                >
                  {client.max_budget && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.78rem",
                        color: "rgba(255,255,255,0.78)",
                      }}
                    >
                      ${client.max_budget.toLocaleString()}
                    </Typography>
                  )}
                  {client.max_budget &&
                    client.min_bedrooms !== undefined &&
                    client.min_bedrooms !== null && (
                      <Box component="span" sx={{ color: "rgba(255,255,255,0.35)", fontSize: "0.78rem" }}>
                        ·
                      </Box>
                    )}
                  {client.min_bedrooms !== undefined &&
                    client.min_bedrooms !== null && (
                      <Tooltip
                        title={
                          client.min_bedrooms === 0
                            ? "Studio acceptable (0+ bedrooms)"
                            : `At least ${client.min_bedrooms} ${
                                client.min_bedrooms === 1 ? "bedroom" : "bedrooms"
                              }`
                        }
                        arrow
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.78rem",
                            color: "rgba(255,255,255,0.78)",
                            borderBottom: "1px dotted rgba(255,255,255,0.25)",
                            cursor: "help",
                          }}
                        >
                          {client.min_bedrooms}+ BR
                        </Typography>
                      </Tooltip>
                    )}
                  {(client.max_budget ||
                    (client.min_bedrooms !== undefined && client.min_bedrooms !== null)) &&
                    clientMoveIn(client) && (
                      <Box component="span" sx={{ color: "rgba(255,255,255,0.35)", fontSize: "0.78rem" }}>
                        ·
                      </Box>
                    )}
                  {clientMoveIn(client) && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.78rem",
                        color: "rgba(255,255,255,0.78)",
                      }}
                    >
                      Move-in{" "}
                      {clientMoveIn(client)}
                    </Typography>
                  )}
                  {client.preferred_areas && client.preferred_areas.length > 0 && (
                    <>
                      <Box component="span" sx={{ color: "rgba(255,255,255,0.35)", fontSize: "0.78rem" }}>
                        ·
                      </Box>
                      {client.preferred_areas.slice(0, 3).map((area, i) => (
                        <Box
                          key={i}
                          component="span"
                          sx={{
                            fontSize: "0.7rem",
                            color: "rgba(255,255,255,0.85)",
                            border: "1px solid rgba(255,255,255,0.2)",
                            borderRadius: 0.75,
                            px: 0.6,
                            py: 0.1,
                          }}
                        >
                          {area}
                        </Box>
                      ))}
                      {client.preferred_areas.length > 3 && (
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.7rem",
                            color: "rgba(255,255,255,0.55)",
                          }}
                        >
                          +{client.preferred_areas.length - 3}
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              )}
            </Box>

            {/* Status pipeline · subtle inset bg distinguishes it as its own
                section · keeps the dark gradient family · Grant directive 2026-05-17 */}
            <Box
              sx={{
                mb: 0.5,
                py: 1.25,
                px: 1.5,
                borderRadius: 1.5,
                backgroundColor: "rgba(0,0,0,0.22)",
                border: "1px solid rgba(255,255,255,0.05)",
              }}
            >
              <StatusPipeline
                currentStatus={client.status}
                onAdvance={(newStatus) => onStatusChange(client, newStatus)}
              />
            </Box>
            {false && (
              <Button
                variant="contained"
                size="small"
              >
                Removed · see StatusPipeline above
              </Button>
            )}

          {/* Subtle separator above action buttons */}
          <Box
            sx={{
              height: "1px",
              backgroundColor: "rgba(255,255,255,0.08)",
              my: 0.85,
            }}
          />

          {/* Action buttons row â€” stretch to fill width */}
          {isMobile && currentUserRole === "agent" ? (
            <Stack
              direction="row"
              spacing={0.75}
              sx={{ width: "100%" }}
              data-tour="agent-client-actions"
            >
              {[
                { key: "call", label: "Call", icon: <PhoneIcon /> },
                { key: "sent_text", label: "Text", icon: <EmailIcon /> },
                { key: "search", label: "Search", icon: <SearchIcon /> },
                { key: "note", label: "Note", icon: <NoteAddIcon /> },
              ].map((action) => (
                <Button
                  key={action.key}
                  size="small"
                  variant={action.key === "search" ? "contained" : "outlined"}
                  onClick={() => {
                    if (action.key === "note") {
                      setAddNoteDialogOpen(true);
                    } else {
                      handleQuickAction(action.key, client);
                    }
                  }}
                  sx={{
                    minWidth: 0,
                    minHeight: 52,
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.15,
                    color: "white",
                    borderColor: "rgba(255,255,255,0.3)",
                    bgcolor:
                      action.key === "search"
                        ? "rgba(59,130,246,0.9)"
                        : "rgba(255,255,255,0.035)",
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    px: 0.5,
                    "& .MuiSvgIcon-root": { fontSize: 18 },
                    "&:hover": {
                      borderColor: "white",
                      bgcolor:
                        action.key === "search"
                          ? "rgba(37,99,235,0.95)"
                          : "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  {action.icon}
                  {action.label}
                </Button>
              ))}
            </Stack>
          ) : isMobile ? (
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{ width: "100%" }}
            >
              <Tooltip title="Log Activity">
                <IconButton
                  size="small"
                  onClick={() => onQuickAction("logActivity", client)}
                  sx={{
                    color: "white",
                    border: "1px solid rgba(255,255,255,0.35)",
                    borderRadius: 1.5,
                    px: 0.9,
                    py: 0.7,
                    flexShrink: 0,
                    "&:hover": {
                      borderColor: "white",
                      backgroundColor: "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  <HistoryIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
              {actions.map((action) => (
                <Button
                  key={action.key}
                  size="small"
                  variant="outlined"
                  startIcon={React.cloneElement(
                    action.icon as React.ReactElement,
                    { sx: { fontSize: 14 } }
                  )}
                  onClick={() => handleQuickAction(action.key, client)}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    color: "white",
                    borderColor: "rgba(255,255,255,0.3)",
                    textTransform: "none",
                    fontSize: "0.74rem",
                    py: 0.55,
                    px: 1.1,
                    whiteSpace: "nowrap",
                    "& .MuiButton-startIcon": { mr: 0.45 },
                    "&:hover": {
                      borderColor: "white",
                      backgroundColor: "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  {getCompactMobileActionLabel(action.key, action.label)}
                </Button>
              ))}
            </Stack>
          ) : (
            <Stack
              direction="row"
              spacing={1}
              sx={{ width: "100%", gap: 1 }}
            >
              <Button
                variant="outlined"
                size="small"
                startIcon={<HistoryIcon sx={{ fontSize: 14 }} />}
                onClick={() => onQuickAction("logActivity", client)}
                sx={{
                  flex: 1,
                  color: "rgba(255,255,255,0.92)",
                  borderColor: "rgba(255,255,255,0.18)",
                  textTransform: "none",
                  fontSize: "0.74rem",
                  fontWeight: 500,
                  py: 0.3,
                  whiteSpace: "nowrap",
                  "& .MuiButton-startIcon": { mr: 0.5 },
                  "&:hover": {
                    borderColor: "rgba(255,255,255,0.45)",
                    backgroundColor: "rgba(255,255,255,0.06)",
                  },
                }}
              >
                Log Activity
              </Button>
              {actions.map((action) => (
                <Button
                  key={action.key}
                  size="small"
                  variant="outlined"
                  startIcon={React.cloneElement(
                    action.icon as React.ReactElement,
                    { sx: { fontSize: 14 } }
                  )}
                  onClick={() => handleQuickAction(action.key, client)}
                  sx={{
                    flex: 1,
                    color: "rgba(255,255,255,0.92)",
                    borderColor: "rgba(255,255,255,0.18)",
                    textTransform: "none",
                    fontSize: "0.74rem",
                    fontWeight: 500,
                    py: 0.3,
                    whiteSpace: "nowrap",
                    "& .MuiButton-startIcon": { mr: 0.5 },
                    "&:hover": {
                      borderColor: "rgba(255,255,255,0.45)",
                      backgroundColor: "rgba(255,255,255,0.06)",
                    },
                  }}
                >
                  {action.label}
                </Button>
              ))}
            </Stack>
          )}
          </Box>
        </Box>


        <Tabs
          value={visibleActiveTab}
          onChange={(_, v) => {
            setActiveTab(v);
            const tab = tabs[v];
            if (tab) onTabChange?.(tab.key);
          }}
          variant="fullWidth"
          centered
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTabs-flexContainer": {
              justifyContent: "space-evenly",
            },
            "& .MuiTab-root": {
              minWidth: 0,
              px: { xs: 0.75, sm: 0.5 },
              fontSize: { xs: "0.78rem", sm: "0.8rem" },
              fontWeight: 600,
              textTransform: "none",
              whiteSpace: "nowrap",
              minHeight: 44,
              flex: 1,
            },
          }}
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.key}
              label={tab.label}
              data-tour={tab.key === "searches" ? "drawer-searches-tab" : undefined}
            />
          ))}
        </Tabs>

        <Box
          sx={{
            p: 2.5,
            overflow: "auto",
            flex: 1,
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {visibleActiveTab === 0 && (
            <ClientTimeline
              taskWorkflowWritesEnabled={taskWorkflowWritesEnabled}
              activities={activities}
              notes={notes}
              tasks={tasks}
              sentListings={sentListings}
              savedSearches={savedSearches}
              sentGuestCards={sentGuestCards}
              invoices={invoices}
              clientWishlist={client?.notes || undefined}
              isLoading={
                loadingActivities ||
                loadingNotes ||
                loadingListings ||
                loadingSearches ||
                loadingGuestCards ||
                loadingInvoices
              }
              loadingTasks={loadingTasks}
              onOpenInvoicePdf={handleViewInvoicePdf}
              onResendInvoice={handleViewInvoicePdf}
              onOpenListingEmail={viewListingEmail}
              onOpenGuestCardEmail={viewGuestCardEmail}
              onAddTask={() => setAddTaskDialogOpen(true)}
              onCompleteTask={(id) => setTaskTransition({ id, action: 'complete' })}
              onSkipTask={(id) => setTaskTransition({ id, action: 'cancel' })}
              onDeleteTask={(id) => setTaskTransition({ id, action: 'cancel' })}
              onAddNote={async (content, activityType) => {
                // PKT-0065: quick-log templates.
                // - activityType undefined (Note default) → current path:
                //   client_notes row + note_added activity row.
                // - activityType set (Left a VM / Texted / Called /
                //   Emailed / Met with) → activities row only with the
                //   chosen activity_type · NO client_notes row (the
                //   activity row IS the note · operator-direct quick-log).
                if (!client) return;
                try {
                  const {
                    data: { user },
                  } = await supabase.auth.getUser();
                  if (!user) return;
                  const clientName = `${client.first_name} ${client.last_name}`;

                  if (activityType) {
                    // Quick-log path: activities-only write.
                    await supabase.from("activities").insert({
                      user_id: user.id,
                      activity_type: activityType,
                      entity_type: "client",
                      entity_id: client.id,
                      metadata: {
                        client_name: clientName,
                        body_preview: content.substring(0, 200),
                        source: "operator_quick_log",
                      },
                    });
                    loadActivities(client.id);
                  } else {
                    // Note default path: client_notes + note_added activity
                    // (unchanged from prior behavior).
                    const { error: noteError } = await supabase
                      .from("client_notes")
                      .insert({
                        client_id: client.id,
                        content,
                        created_by: user.id,
                      });
                    if (noteError) throw noteError;
                    await supabase.from("activities").insert({
                      user_id: user.id,
                      activity_type: "note_added",
                      entity_type: "client",
                      entity_id: client.id,
                      metadata: {
                        client_name: clientName,
                        note_preview: content.substring(0, 100),
                      },
                    });
                    loadNotes(client.id);
                    loadActivities(client.id);
                  }
                } catch (err) {
                  console.error("Error adding inline note:", err);
                }
              }}
            />
          )}
          {visibleActiveTab === 1 && (
            <Stack spacing={2}>
              {process.env.REACT_APP_INTENT_PROFILE_UI_ENABLED === "true" && (
                <IntentProfilePanel profile={intentProfile} />
              )}
              <ClientShortlistPanel
                client={client}
                sentListings={sentListings}
                savedSearches={savedSearches}
              />
            </Stack>
          )}
          {visibleActiveTab === 2 && (
            <ClientFilesPanel
              clientId={client.id}
              sentListings={sentListings}
              sentGuestCards={sentGuestCards}
              invoices={invoices}
              isLoading={loadingListings || loadingGuestCards || loadingInvoices}
              onOpenListingEmail={viewListingEmail}
              onOpenGuestCardEmail={viewGuestCardEmail}
              onOpenInvoicePdf={handleViewInvoicePdf}
            />
          )}
          {visibleActiveTab === 3 && canManageAssignments && (
            <ClientMessagesPanel
              clientId={client.id}
              clientName={`${client.first_name} ${client.last_name}`.trim()}
            />
          )}
        </Box>
      </Drawer>

      {/* Add Note Dialog */}
      <Dialog
        open={addNoteDialogOpen}
        onClose={() => setAddNoteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Note</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Enter note..."
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddNoteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddNote}
            disabled={!newNote.trim()}
          >
            Save Note
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Task Dialog */}
      <Dialog
        open={addTaskDialogOpen}
        onClose={() => setAddTaskDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pr: 7 }}>
          <Tooltip title="Close"><IconButton aria-label="Close" onClick={() => setAddTaskDialogOpen(false)} sx={{ position: "absolute", right: 8, top: 8, width: 40, height: 40 }}><CloseIcon sx={{ fontSize: 20 }} /></IconButton></Tooltip>
          <Box display="flex" alignItems="center" gap={1}>
            <TaskIcon color="primary" />
            Add Task
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Task Type *</InputLabel>
              <Select
                value={newTaskType}
                label="Task Type *"
                onChange={(e) => setNewTaskType(e.target.value)}
              >
                {taskTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Task Title *"
              placeholder="e.g., Follow up on application"
              value={newTaskTitle}
              onChange={(e) => setNewTaskTitle(e.target.value)}
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Description"
              placeholder="Additional details..."
              value={newTaskDescription}
              onChange={(e) => setNewTaskDescription(e.target.value)}
            />
            <TextField
              fullWidth
              type="date"
              label="Due Date"
              value={newTaskDueDate}
              onChange={(e) => setNewTaskDueDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddTaskDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddTask}
            disabled={!newTaskTitle.trim() || !newTaskType}
          >
            Add Task
          </Button>
        </DialogActions>
      </Dialog>

      {/* PKT-0043a v1a regression fix · Agent reassignment Dialog
          ============================================================
          The old activeTab===0 Notes content carried an inline manager
          agent-change form. When the drawer tabs were simplified, that mount
          site was deleted but the handlers + state
          stayed orphaned. This Dialog re-wires them so managers can still
          reassign client agents from the sidebar (the Zone 3 agent click
          opens this · matches Director #6 PR #109 "agent in Zone 3" intent). */}
      <Dialog
        open={agentEditOpen}
        onClose={() => {
          if (savingAgent) return;
          handleCancelAgentChange();
          setAgentEditOpen(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Change assigned agent</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="assigned-agent-label">Agent</InputLabel>
              <Select
                labelId="assigned-agent-label"
                value={selectedAgentId}
                label="Agent"
                onChange={(e) => handleAgentChange(e.target.value as string)}
                disabled={savingAgent}
              >
                <MenuItem value="">
                  <em>Unassigned</em>
                </MenuItem>
                {agents.map((agent) => (
                  <MenuItem key={agent.id} value={agent.id}>
                    {agent.full_name || agent.email || agent.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {!agentChanged && (
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", display: "block", mt: 1 }}
              >
                Pick a different agent to enable Save.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              handleCancelAgentChange();
              setAgentEditOpen(false);
            }}
            disabled={savingAgent}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              await handleSaveAgent();
              setAgentEditOpen(false);
            }}
            disabled={!agentChanged || savingAgent}
            startIcon={
              savingAgent ? <CircularProgress size={16} /> : undefined
            }
          >
            {savingAgent ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Preview Modal */}
      <EmailPreviewModal
        open={emailPreviewOpen}
        onClose={() => setEmailPreviewOpen(false)}
        title={selectedEmail?.title || "Email Preview"}
        emailHtml={selectedEmail?.html}
        metadata={selectedEmail?.metadata}
      />

      {/* Send Invoice Modal */}
      <SendInvoiceModal
        open={sendInvoiceOpen}
        onClose={() => setSendInvoiceOpen(false)}
        client={client}
        onSuccess={handleInvoiceSuccess}
        currentAgent={currentAgent}
      />

      {/* Demo Invoice Disabled Dialog */}
      <Dialog
        open={demoInvoiceDialogOpen}
        onClose={() => setDemoInvoiceDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Invoicing</DialogTitle>
        <DialogContent>
          <Alert severity="info">
            Invoice generation is disabled in demo mode. In production, invoices
            are automatically generated as PDFs and emailed to your client.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDemoInvoiceDialogOpen(false)}>Got it</Button>
        </DialogActions>
      </Dialog>

      {/* Invoice PDF Viewer Modal */}
      <InvoicePdfModal
        open={invoicePdfModalOpen}
        onClose={() => {
          setInvoicePdfModalOpen(false);
          setSelectedInvoice(null);
        }}
        invoice={selectedInvoice}
        onStatusChange={handleInvoiceStatusChange}
      />
    </>
  );
};

export default DetailDrawer;
